import type { MedplumClient } from '@medplum/core';
import { config } from '../config';
import { resolveOrganizationIdFromEpisode } from './resolveEpisodeOrganization';
import type { EpisodeParty } from '../pages/clients/data/types';
import { manageEocParty, type ManageEocPartyBotInput } from './adminActionsService';
import { UserFacingError } from './userFacingError';

export function parseEpisodePartyId(partyId: string): {
  kind: 'patient' | 'relatedperson' | 'contact';
  patientId?: string;
  relatedPersonId?: string;
} {
  if (partyId.startsWith('relatedperson-')) {
    return { kind: 'relatedperson', relatedPersonId: partyId.replace('relatedperson-', '') };
  }
  if (partyId.startsWith('contact-')) {
    return { kind: 'contact' };
  }
  return { kind: 'patient', patientId: partyId };
}

async function resolveOrgId(
  medplum: MedplumClient,
  primaryPatientId: string,
  episodeId: string,
): Promise<string> {
  return resolveOrganizationIdFromEpisode(medplum, primaryPatientId, episodeId);
}

function baseBotInput(
  episodeId: string,
  primaryPatientId: string,
  orgId: string,
): Pick<ManageEocPartyBotInput, 'action' | 'episodeId' | 'primaryPatientId' | 'orgId' | 'projectId'> {
  return {
    action: 'manage-eoc-party',
    episodeId,
    primaryPatientId,
    orgId,
    projectId: config.medplum.projectId,
  };
}

export async function addExistingClientToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    participantPatientId: string;
    relationship?: string;
  },
): Promise<void> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'add-participant-existing',
    participantPatientId: opts.participantPatientId,
    relationship: opts.relationship,
  });
}

export async function addNewClientToEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    firstName: string;
    lastName: string;
    email: string;
    relationship?: string;
  },
): Promise<{ relatedPersonId: string }> {
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const result = await manageEocParty(medplum, {
    ...baseBotInput(opts.episodeId, opts.primaryPatientId, orgId),
    operation: 'add-participant-new',
    firstName: opts.firstName,
    lastName: opts.lastName,
    email: opts.email,
    relationship: opts.relationship,
  });
  if (!result.relatedPersonId) {
    throw new Error('Contact was created but no related person id was returned.');
  }
  return { relatedPersonId: result.relatedPersonId };
}

export async function removeParticipantFromEpisode(
  medplum: MedplumClient,
  opts: {
    episodeId: string;
    primaryPatientId: string;
    member: EpisodeParty;
  },
): Promise<void> {
  const parsed = parseEpisodePartyId(opts.member.id);
  const orgId = await resolveOrgId(medplum, opts.primaryPatientId, opts.episodeId);
  const base = baseBotInput(opts.episodeId, opts.primaryPatientId, orgId);

  if (parsed.kind === 'patient' && parsed.patientId) {
    await manageEocParty(medplum, {
      ...base,
      operation: 'remove-participant',
      participantPatientId: parsed.patientId,
    });
    return;
  }

  const relatedPersonId = parsed.relatedPersonId ?? opts.member.relatedPersonId;
  if (!relatedPersonId) {
    throw new UserFacingError('Only linked participants can be removed from the episode.');
  }
  await manageEocParty(medplum, {
    ...base,
    operation: 'remove-participant',
    relatedPersonId,
  });
}
